import React from "react";
import ViewDocument from "../../ui/ViewDocument";

const CareIndonesia = () => {
  return (
    <div
      id="care-economy-indonesia"
      className="space-y-4 text-softBlack leading-loose text-base font-normal !mt-14 scroll-mt-24"
    >
      <h2 className="text-xl font-bold !mt-10 mb-4">
        The care economy in Indonesia
      </h2>

      <h3 className="text-base font-bold mt-4 mb-3">
        Current status of the care economy
      </h3>
      <p>
        The Ministry of Women Empowerment and Child Protection, Indonesia, has
        been working since 2022, in collaboration with the ILO, to create a
        roadmap for 2025-2045 on the care economy. While the{" "}
        <a
          href="https://www.ilo.org/meetings-and-events/launch-road-map-care-economy-indonesia"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          roadmap was launched
        </a>{" "}
        by the Ministry of Women’s Empowerment and Child Protection (KPPPA) on
        March 28, 2024, the text has not been made publicly available. The
        available text indicates that it prioritizes seven issues concerning the
        care economy. These include:
      </p>
      <ul className="list-decimal list-outside space-y-2 ml-6">
        <li>Developing accessible, high-quality childcare services</li>
        <li>Strengthening elderly and long-term care services</li>
        <li>
          Improving inclusive, integrated care services for people with
          disabilities, HIV, special needs, and other vulnerable groups
        </li>
        <li>Enhancing greater access to maternity leave</li>
        <li>
          Increasing the involvement of men, including through paternity leave
        </li>
        <li>Recognizing decent work for care workers</li>
        <li>
          Implementing social protection for those workers in the care economy
        </li>
      </ul>

      <h3 className="text-base font-semibold italic">
        Women’s participation in the labor force
      </h3>
      <p>
        The World Bank published a{" "}
        <a
          href="https://openknowledge.worldbank.org/bitstreams/488b7505-c579-4478-a41e-c9a4b9ce6530/download"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          report
        </a>{" "}
        in 2024 titled The Care Economy in Indonesia: A Pathway for Women’s
        Economic Participation and Social Well-being, which draws on primary and
        secondary data to present the current status of women’s labor force
        participation in Indonesia, the hours spent on caregiving, the gaps in
        the current child care system in the country, and policy recommendations
        to improve the participation of women in the workforce and improve the
        conditions of care work.
      </p>
      <p>
        The World Bank report cites ILO data on the female labor force
        participation rate. Women’s participation has remained at 53 percent for
        about 20 years, while male labor force participation stands at 81
        percent. The World Bank also cites data on hours spent on childcare,
        with mothers spending an average of 13.7 hours per day, compared to 3.8
        hours spent by fathers, which is lower than 4.2 hours spent by household
        family members, and 3–4 hours by non-household family members and
        non-family members, respectively. Another{" "}
        <a
          href="https://investinginwomen.asia/wp-content/uploads/2023/09/Brief_Time-use-and-agency_1.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          analysis
        </a>{" "}
        carried out by a consortium of international organizations in 2023 found
        that women spend 2.8 times more time on unpaid domestic and care work
        than men. Women spent about 8.1 hours a day on unpaid care and domestic
        work and about 12.3 hours if they had children under the age of five
        years. Men, on the other hand, spent about 2.9 hours on unpaid care and
        domestic work.
      </p>
      <p>
        According to the World Bank report, care provision in Indonesia consists
        of largely informal arrangements with unpaid carers, with institutional
        care for children aged 0-6 years often being provided through
        privately-run Early Childhood Education and Development (ECED) centers,
        which vary in the standard of services provided, and often only provide
        3-4 hours of childcare per day.
      </p>
      <p>
        Creating a childcare support system which provides 8 hours or more of
        care every day is associated with a 29 percentage point increase in
        women’s labor force participation rate, according to the World Bank.
        This can also improve the likelihood of mothers having a formal job by
        15 percentage points, working full time by 20 percentage points, and a
        66 percent increase in earnings.
      </p>

      <h3 className="text-base italic font-bold !mt-5">
        Prioritizing childcare first
      </h3>
      <p>
        The World Bank report focuses primarily on childcare, referencing a
        statement from the Government of Indonesia, according to which ECED is
        the main priority under the first phase of the roadmap’s implementation
        between 2025 and 2029.
      </p>
      <p>
        The second phase, from 2030-2034, will involve the roll-out of policies
        and regulations strengthening the care economy, while the third
        (2035-2039) will improve access to services and improve care worker
        conditions. The final phase will focus on institutionalization of care
        service delivery.
      </p>

      <h3 className="text-base font-bold !mt-5">
        Barriers to a strong care economy
      </h3>
      <p>
        According to the World Bank report, Indonesia does not have an enabling
        environment for paid or unpaid caregivers, as:
      </p>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          There is a small skilled care workforce, which does not receive
          adequate compensation and has limited rights
        </li>
        <li>There is no recognition of unpaid care work</li>
        <li>
          Private entities have little incentive to improve the quality of
          services
        </li>
        <li>
          Maternity, paternity, and parental leave benefits are inadequate
        </li>
      </ul>

      <h3 className="text-base font-bold !mt-5">Expert recommendations</h3>
      <p>The World Bank report recommends:</p>
      <ul className="list-decimal list-outside space-y-2 ml-6">
        <li>Improving Availability and Access to Childcare Services</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Expanding operating hours of institutional childcare providers and
            aligning them with working hours
          </li>
          <li>Regulating and paid care services provided at homes</li>
          <li>
            The government can financially support community-based models which
            combine institutional, home-based and family care
          </li>
          <li>
            Increasing community awareness on locally available childcare
            services
          </li>
        </ul>
        <li>Making care affordable for all</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            The government can provide tax breaks, subsidies and tax credits to
            persons availing childcare
          </li>
          <li>
            Persons who find it difficult to afford childcare may also be
            offered nutrition support, school supplies and other non-monetary
            incentives
          </li>
          <li>
            The government can support formal and informal care providers
            through schemes, tax breaks, and other incentives
          </li>
        </ul>
        <li>Promoting quality of care services</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Enforcing standards that regulate quality of care to be provided for
            all institutional care providers
          </li>
          <li>
            Establishing safety and cleanliness standards to improve confidence
            in care providers
          </li>
          <li>Standardizing ECED teacher training qualifications</li>
          <li>
            Providing social protections to both formal and informal
            (home-based) care providers
          </li>
        </ul>
        <li>Coordination of responsible agencies and stakeholders</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Coordination between responsible government agencies at national and
            subnational level is essential to improve regulation, enforcement,
            financial support, and labor protections
          </li>
          <li>
            Adequate financing is to be allotted, not only to formal
            institutions, but also alternative arrangements such as home-based
            and community-based care
          </li>
          <li>
            More effective utilization of existing funds allocated to childcare,
            including disbursement of funds at the right level
          </li>
        </ul>
        <li>Addressing social norms around caregiving</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Conducting public campaigns to shift community perceptions towards
            equitable gender roles in caregiving
          </li>
          <li>
            Improving women’s representation in local governance to challenge
            existing gender norms
          </li>
          <li>
            Addressing discriminatory practices and gender-based norms through a
            comprehensive approach including legal and community-based aspects
          </li>
        </ul>
        <li>Improving workplace policies</li>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Expanding maternity and paternity leave provisions for workers in
            the formal and informal sector, and adding support for mothers to
            transition back to the workplace
          </li>
          <li>
            Public and private sector coordination on flexible working
            arrangements backed by policy
          </li>
          <li>
            Employee benefits such as on-site childcare, vouchers and
            breastfeeding support and facilities
          </li>
        </ul>
      </ul>

      <ViewDocument
        description="This document contains a list of schemes, policies and practices in Indonesia, which directly and indirectly benefit caregivers and their dependents"
        documentUrl="https://sapient-dev-bucket-projects.s3.ap-south-1.amazonaws.com/noora-health/uploads/34555752216158845593._Care-Economy---Indonesia---Schemes-and-policies-supporting-caregivers.pdf"
        className="!my-8"
      />
    </div>
  );
};

export default CareIndonesia;
